// =============================================================================
// ImageToolsPanel
// =============================================================================
// Platform admin form for the ImageToolsSettings singleton — the AI upscale /
// background removal feature.
//
// Two things here are operational levers rather than preferences:
//   • The master switch is the kill switch. Turning it off stops every job on
//     every surface immediately, which is what you reach for when a provider
//     starts billing wrongly or returning garbage.
//   • The model ids are editable so a provider model can be swapped without a
//     deploy. That is deliberately powerful, hence `settings:manage`.
// =============================================================================

import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Input, Spinner } from '@tkpod/ui';

import { api } from '@/lib/api';

/** `-1` means unlimited, matching the other plan limits. */
const UNLIMITED = -1;

interface ImageToolsSettings {
  enabled: boolean;
  upscaleEnabled: boolean;
  bgRemovalEnabled: boolean;
  vectorizeEnabled: boolean;
  guestAccessEnabled: boolean;
  guestDailyLimit: number;
  defaultMonthlyLimit: number;
  refundOnDiscard: boolean;
  upscaleModel: string;
  bgRemovalModel: string;
  vectorizeModel: string;
}

interface UsageSummary {
  jobsThisMonth: number;
  failedThisMonth: number;
  discardedThisMonth: number;
  inFlight: number;
}

interface SettingsResponse {
  data: ImageToolsSettings;
}
interface UsageResponse {
  data: UsageSummary;
}

export function ImageToolsPanel() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<SettingsResponse>({
    queryKey: ['admin', 'image-tools', 'settings'],
    queryFn: () => api.get<SettingsResponse>('/api/v1/admin/image-tools/settings'),
  });

  const { data: usage } = useQuery<UsageResponse>({
    queryKey: ['admin', 'image-tools', 'usage'],
    queryFn: () => api.get<UsageResponse>('/api/v1/admin/image-tools/usage'),
    // Refresh while jobs are moving so a stalled worker is visible without a
    // manual reload.
    refetchInterval: (q) =>
      ((q.state.data as UsageResponse | undefined)?.data.inFlight ?? 0) > 0 ? 10_000 : false,
  });

  const [form, setForm] = useState<ImageToolsSettings | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Hydrate once; later refetches must not discard an admin's in-progress edits.
  useEffect(() => {
    if (form === null && data?.data) setForm(data.data);
  }, [data, form]);

  const updateMut = useMutation({
    mutationFn: async (body: ImageToolsSettings) =>
      api.patch<SettingsResponse>('/api/v1/admin/image-tools/settings', body),
    onSuccess: async (res) => {
      setForm(res.data);
      setSaveError(null);
      setSaved(true);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'image-tools', 'settings'],
      });
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings');
    },
  });

  // Error must be checked before the spinner: on a failed initial load `form`
  // never hydrates, so an `isLoading || !form` gate alone would spin forever
  // and make this branch unreachable. Scoped to `!form` so a failed background
  // refetch does not discard an admin's in-progress edits.
  if (error && !form) {
    return (
      <Card>
        <CardHeader title="Image Tools" description={DESCRIPTION} />
        <div className="mt-4 rounded-md bg-error-muted p-4 text-sm text-error">
          Failed to load: {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  if (isLoading || !form) {
    return (
      <Card>
        <CardHeader title="Image Tools" description={DESCRIPTION} />
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      </Card>
    );
  }

  const onToggle =
    (key: keyof ImageToolsSettings) => (e: ChangeEvent<HTMLInputElement>) => {
      setSaved(false);
      setForm((prev) => (prev ? { ...prev, [key]: e.target.checked } : prev));
    };

  const onNumber =
    (key: keyof ImageToolsSettings) => (e: ChangeEvent<HTMLInputElement>) => {
      setSaved(false);
      const parsed = Number.parseInt(e.target.value, 10);
      setForm((prev) =>
        prev ? { ...prev, [key]: Number.isFinite(parsed) ? parsed : 0 } : prev,
      );
    };

  const onText =
    (key: keyof ImageToolsSettings) => (e: ChangeEvent<HTMLInputElement>) => {
      setSaved(false);
      setForm((prev) => (prev ? { ...prev, [key]: e.target.value } : prev));
    };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form) return;
    setSaveError(null);
    updateMut.mutate(form);
  };

  return (
    <Card>
      <CardHeader title="Image Tools" description={DESCRIPTION} />

      {!form.enabled && (
        <div className="mt-4 rounded-md border border-warning-border bg-warning-muted p-4 text-sm text-warning">
          <strong className="font-semibold">Feature is off.</strong> Designers
          hide the tools entirely and no jobs run, regardless of plan
          entitlements or per-store settings.
        </div>
      )}

      {usage?.data && (
        <dl className="mt-4 grid grid-cols-4 gap-4">
          <UsageStat label="Jobs this month" value={usage.data.jobsThisMonth} />
          <UsageStat label="Failed" value={usage.data.failedThisMonth} />
          <UsageStat label="Discarded" value={usage.data.discardedThisMonth} />
          <UsageStat label="In flight" value={usage.data.inFlight} />
        </dl>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-6">
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-text-primary">
            Availability
          </legend>
          <Toggle
            label="Enable Image Tools"
            hint="Master switch. Off stops every job on every surface."
            checked={form.enabled}
            onChange={onToggle('enabled')}
          />
          <Toggle
            label="Upscale"
            checked={form.upscaleEnabled}
            onChange={onToggle('upscaleEnabled')}
          />
          <Toggle
            label="Remove background"
            checked={form.bgRemovalEnabled}
            onChange={onToggle('bgRemovalEnabled')}
          />
          <Toggle
            label="Vectorize"
            hint="AI tracing to vector shapes, rendered back to a high-resolution PNG."
            checked={form.vectorizeEnabled}
            onChange={onToggle('vectorizeEnabled')}
          />
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-text-primary">
            Guests
          </legend>
          <Toggle
            label="Allow anonymous designer sessions"
            hint="The standalone designer is open to visitors with no account."
            checked={form.guestAccessEnabled}
            onChange={onToggle('guestAccessEnabled')}
          />
          <Input
            label={`Guest daily limit (${UNLIMITED} = unlimited)`}
            type="number"
            value={String(form.guestDailyLimit)}
            onChange={onNumber('guestDailyLimit')}
          />
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-text-primary">
            Quota
          </legend>
          <Input
            label={`Default monthly limit (${UNLIMITED} = unlimited)`}
            type="number"
            value={String(form.defaultMonthlyLimit)}
            onChange={onNumber('defaultMonthlyLimit')}
          />
          <p className="text-xs text-text-tertiary">
            Applied to organizations whose plan leaves the Image Tools limit
            unset. A plan-level limit always wins.
          </p>
          <Toggle
            label="Refund on discard"
            hint="Discarding a result in the before/after confirmation credits the metered unit back. The provider has still been paid — this is customer goodwill, not cost recovery."
            checked={form.refundOnDiscard}
            onChange={onToggle('refundOnDiscard')}
          />
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-text-primary">
            Provider models
          </legend>
          <Input
            label="Upscale model"
            value={form.upscaleModel}
            onChange={onText('upscaleModel')}
            required
          />
          <Input
            label="Background removal model"
            value={form.bgRemovalModel}
            onChange={onText('bgRemovalModel')}
            required
          />
          <Input
            label="Vectorize model"
            value={form.vectorizeModel}
            onChange={onText('vectorizeModel')}
            required
          />
          <p className="text-xs text-text-tertiary">
            Changing a model takes effect on the next job — no deploy needed.
            New jobs only; work already queued finishes on the old model.
          </p>
        </fieldset>

        {saveError && (
          <div className="rounded-md bg-error-muted p-3 text-sm text-error">
            {saveError}
          </div>
        )}
        {saved && !updateMut.isPending && (
          <div className="rounded-md bg-success-muted p-3 text-sm text-success">
            Settings saved.
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={updateMut.isPending}>
            {updateMut.isPending ? 'Saving…' : 'Save settings'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={updateMut.isPending}
            onClick={() => {
              if (data?.data) setForm(data.data);
              setSaveError(null);
              setSaved(false);
            }}
          >
            Reset
          </Button>
        </div>
      </form>
    </Card>
  );
}

const DESCRIPTION =
  'AI upscale, background removal and vectorize for the designer. Platform-wide switches, guest policy and quota defaults.';

function UsageStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border-subtle p-3">
      <dt className="text-xs text-text-tertiary">{label}</dt>
      <dd className="mt-1 text-lg font-semibold text-text-primary">{value}</dd>
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 rounded border-border-subtle"
      />
      <span>
        <span className="text-sm text-text-primary">{label}</span>
        {hint && <span className="block text-xs text-text-tertiary">{hint}</span>}
      </span>
    </label>
  );
}
